/**
 * 多角形の穴: 内外判定、自己交差、耳切り＋橋渡し、重なり穴の和。
 */

export interface Point2D {
  x: number;
  y: number;
}

const EPS = 1e-9;

export function signedArea2(points: Point2D[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    area += a.x * b.y - b.x * a.y;
  }
  return area / 2;
}

export function ensureWinding(points: Point2D[], ccw: boolean): Point2D[] {
  const area = signedArea2(points);
  if (Math.abs(area) < EPS) return points;
  const isCcw = area > 0;
  return isCcw === ccw ? points : [...points].reverse();
}

function onSegment(point: Point2D, a: Point2D, b: Point2D): boolean {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const length = Math.hypot(abx, aby);
  if (length < EPS) return Math.hypot(point.x - a.x, point.y - a.y) < EPS;
  const t = ((point.x - a.x) * abx + (point.y - a.y) * aby) / (length * length);
  if (t < -1e-9 || t > 1 + 1e-9) return false;
  const closestX = a.x + abx * Math.min(1, Math.max(0, t));
  const closestY = a.y + aby * Math.min(1, Math.max(0, t));
  return Math.hypot(point.x - closestX, point.y - closestY) < 1e-6;
}

export function pointInPolygon(point: Point2D, polygon: Point2D[], includeBoundary = true): boolean {
  const n = polygon.length;
  if (n < 3) return false;
  if (includeBoundary) {
    for (let i = 0; i < n; i++) {
      if (onSegment(point, polygon[i], polygon[(i + 1) % n])) return true;
    }
  }
  let inside = false;
  for (let i = 0; i < n; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % n];
    if (a.y > point.y !== b.y > point.y) {
      const atX = ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
      if (point.x < atX) inside = !inside;
    }
  }
  return inside;
}

function orient(a: Point2D, b: Point2D, c: Point2D): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function segmentsIntersect(a: Point2D, b: Point2D, c: Point2D, d: Point2D, proper: boolean): boolean {
  const o1 = orient(a, b, c);
  const o2 = orient(a, b, d);
  const o3 = orient(c, d, a);
  const o4 = orient(c, d, b);
  if (proper) {
    return o1 * o2 < 0 && o3 * o4 < 0;
  }
  if (o1 * o2 < 0 && o3 * o4 < 0) return true;
  if (Math.abs(o1) < EPS && onSegment(c, a, b)) return true;
  if (Math.abs(o2) < EPS && onSegment(d, a, b)) return true;
  if (Math.abs(o3) < EPS && onSegment(a, c, d)) return true;
  if (Math.abs(o4) < EPS && onSegment(b, c, d)) return true;
  return false;
}

export function polygonSelfIntersects(points: Point2D[]): boolean {
  const n = points.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      if (Math.abs(i - j) <= 1 || (i === 0 && j === n - 1) || (j === 0 && i === n - 1)) continue;
      const adj = (j + 1) % n === i || (i + 1) % n === j;
      if (adj) continue;
      if (segmentsIntersect(a, b, points[j], points[(j + 1) % n], false)) return true;
    }
  }
  return false;
}

/** 内側の輪郭が外側に完全に収まる。辺上は可。交差して外へ出る・外側を囲むは不可。 */
export function polygonInsidePolygon(inner: Point2D[], outer: Point2D[]): boolean {
  if (inner.length < 3 || outer.length < 3) return false;
  if (Math.abs(signedArea2(inner)) < 1e-12) return false;
  for (const point of inner) {
    if (!pointInPolygon(point, outer, true)) return false;
  }
  const n = inner.length;
  const m = outer.length;
  for (let i = 0; i < n; i++) {
    const a = inner[i];
    const b = inner[(i + 1) % n];
    for (let j = 0; j < m; j++) {
      if (segmentsIntersect(a, b, outer[j], outer[(j + 1) % m], true)) return false;
    }
  }
  const outerSample = {
    x: outer.reduce((s, p) => s + p.x, 0) / m,
    y: outer.reduce((s, p) => s + p.y, 0) / m,
  };
  if (pointInPolygon(outerSample, inner, false) && !pointInPolygon(inner[0], outer, false)) {
    return false;
  }
  return true;
}

export function polygonsOverlap(a: Point2D[], b: Point2D[]): boolean {
  if (a.some((p) => pointInPolygon(p, b, false)) || b.some((p) => pointInPolygon(p, a, false))) {
    return true;
  }
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      if (segmentsIntersect(a[i], a[(i + 1) % a.length], b[j], b[(j + 1) % b.length], true)) {
        return true;
      }
    }
  }
  return false;
}

function segmentIntersection(
  a: Point2D,
  b: Point2D,
  c: Point2D,
  d: Point2D
): Point2D | null {
  const r = { x: b.x - a.x, y: b.y - a.y };
  const s = { x: d.x - c.x, y: d.y - c.y };
  const den = r.x * s.y - r.y * s.x;
  if (Math.abs(den) < EPS) return null;
  const qp = { x: c.x - a.x, y: c.y - a.y };
  const t = (qp.x * s.y - qp.y * s.x) / den;
  const u = (qp.x * r.y - qp.y * r.x) / den;
  if (t < -1e-9 || t > 1 + 1e-9 || u < -1e-9 || u > 1 + 1e-9) return null;
  return { x: a.x + r.x * t, y: a.y + r.y * t };
}

function same2(a: Point2D, b: Point2D): boolean {
  return Math.hypot(a.x - b.x, a.y - b.y) < 1e-6;
}

/** 凸包（Andrew）。重なる穴を1輪郭にまとめるときの粗い和。 */
export function convexHull(points: Point2D[]): Point2D[] {
  const uniq: Point2D[] = [];
  for (const point of points) {
    if (!uniq.some((existing) => same2(existing, point))) uniq.push(point);
  }
  if (uniq.length < 3) return uniq;
  const sorted = [...uniq].sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));
  const lower: Point2D[] = [];
  for (const point of sorted) {
    while (lower.length >= 2 && orient(lower[lower.length - 2], lower[lower.length - 1], point) <= 0) {
      lower.pop();
    }
    lower.push(point);
  }
  const upper: Point2D[] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const point = sorted[i];
    while (upper.length >= 2 && orient(upper[upper.length - 2], upper[upper.length - 1], point) <= 0) {
      upper.pop();
    }
    upper.push(point);
  }
  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

/** 重なる穴を凸包で1つにまとめる。重なり分もくり抜く。 */
export function mergeOverlappingHoles(holes: Point2D[][]): Point2D[][] {
  const rings = holes
    .map((hole) => hole.filter((_, i, arr) => !same2(hole[i], arr[(i + 1) % arr.length])))
    .filter((hole) => hole.length >= 3 && Math.abs(signedArea2(hole)) >= 1e-12);
  let changed = true;
  while (changed) {
    changed = false;
    outer: for (let i = 0; i < rings.length; i++) {
      for (let j = i + 1; j < rings.length; j++) {
        if (!polygonsOverlap(rings[i], rings[j])) continue;
        rings[i] = convexHull([...rings[i], ...rings[j]]);
        rings.splice(j, 1);
        changed = true;
        break outer;
      }
    }
  }
  return rings;
}

function isPointInTriangle(p: Point2D, a: Point2D, b: Point2D, c: Point2D): boolean {
  const d1 = (p.x - b.x) * (a.y - b.y) - (a.x - b.x) * (p.y - b.y);
  const d2 = (p.x - c.x) * (b.y - c.y) - (b.x - c.x) * (p.y - c.y);
  const d3 = (p.x - a.x) * (c.y - a.y) - (c.x - a.x) * (p.y - a.y);
  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNeg && hasPos);
}

/**
 * 単純多角形（穴なし）を耳切り法で三角形分割する。
 * 返り値は入力配列に対するインデックス三つ組の並びで、反時計回りに揃えられる。
 */
export function triangulatePolygon(points: Point2D[]): number[] {
  const n = points.length;
  if (n < 3) return [];
  if (n === 3) return signedArea2(points) >= 0 ? [0, 1, 2] : [0, 2, 1];

  const ccw = signedArea2(points) >= 0;
  const remaining: number[] = [];
  for (let i = 0; i < n; i++) remaining.push(ccw ? i : n - 1 - i);

  const triangles: number[] = [];
  let guard = 0;
  while (remaining.length > 3 && guard < n * n) {
    guard++;
    let earFound = false;
    for (let i = 0; i < remaining.length; i++) {
      const prev = remaining[(i - 1 + remaining.length) % remaining.length];
      const cur = remaining[i];
      const next = remaining[(i + 1) % remaining.length];
      const a = points[prev];
      const b = points[cur];
      const c = points[next];
      const cross = (b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y);
      if (cross <= 0) continue;
      let contains = false;
      for (const idx of remaining) {
        if (idx === prev || idx === cur || idx === next) continue;
        if (isPointInTriangle(points[idx], a, b, c)) {
          contains = true;
          break;
        }
      }
      if (contains) continue;
      triangles.push(prev, cur, next);
      remaining.splice(i, 1);
      earFound = true;
      break;
    }
    if (!earFound) break;
  }

  for (let i = 1; i + 1 < remaining.length; i++) {
    triangles.push(remaining[0], remaining[i], remaining[i + 1]);
  }
  return triangles;
}

function visible(from: Point2D, to: Point2D, polygon: Point2D[], holes: Point2D[][]): boolean {
  const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
  if (!pointInPolygon(mid, polygon, true)) return false;
  for (const hole of holes) {
    if (pointInPolygon(mid, hole, false)) return false;
  }
  const rings = [polygon, ...holes];
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      if (same2(a, from) || same2(b, from) || same2(a, to) || same2(b, to)) continue;
      if (segmentsIntersect(from, to, a, b, true)) return false;
      const hit = segmentIntersection(from, to, a, b);
      if (hit && !same2(hit, from) && !same2(hit, to)) return false;
    }
  }
  return true;
}

/**
 * 穴を外側へ橋渡しして1つの多角形にし、耳切りする。
 * 返り値は結合後の頂点配列に対するインデックス。
 */
export function triangulatePolygonWithHoles(
  outer: Point2D[],
  holes: Point2D[][]
): { vertices: Point2D[]; indices: number[] } {
  let ring = ensureWinding(outer, true);
  const mergedHoles = mergeOverlappingHoles(holes)
    .map((hole) => ensureWinding(hole, false))
    .filter((hole) => Math.abs(signedArea2(hole)) >= 1e-12);
  const remaining = [...mergedHoles];

  while (remaining.length > 0) {
    let bestHole = 0;
    let bestX = -Infinity;
    let bestVertex = 0;
    for (let h = 0; h < remaining.length; h++) {
      for (let i = 0; i < remaining[h].length; i++) {
        if (remaining[h][i].x > bestX) {
          bestX = remaining[h][i].x;
          bestHole = h;
          bestVertex = i;
        }
      }
    }
    const hole = remaining[bestHole];
    const holePoint = hole[bestVertex];
    let bridge = -1;
    let bridgeDist = Infinity;
    for (let i = 0; i < ring.length; i++) {
      if (ring[i].x < holePoint.x - 1e-9) continue;
      if (!visible(holePoint, ring[i], ring, remaining)) continue;
      const dist = Math.hypot(ring[i].x - holePoint.x, ring[i].y - holePoint.y);
      if (dist < bridgeDist) {
        bridgeDist = dist;
        bridge = i;
      }
    }
    if (bridge < 0) {
      remaining.splice(bestHole, 1);
      continue;
    }
    const rotated = [...hole.slice(bestVertex), ...hole.slice(0, bestVertex), hole[bestVertex]];
    ring = [...ring.slice(0, bridge + 1), ...rotated, ring[bridge], ...ring.slice(bridge + 1)];
    remaining.splice(bestHole, 1);
  }

  return { vertices: ring, indices: triangulateSolidRing(ring, outer, mergedHoles) };
}

/** 橋渡し後の輪郭を耳切りする。穴の中に入る三角形は切らない。扇分割はしない。 */
function triangulateSolidRing(points: Point2D[], outer: Point2D[], holes: Point2D[][]): number[] {
  const n = points.length;
  if (n < 3) return [];
  const remaining: number[] = [];
  for (let i = 0; i < n; i++) remaining.push(i);

  const inSolid = (p: Point2D) =>
    pointInPolygon(p, outer, true) && !holes.some((hole) => pointInPolygon(p, hole, false));

  const triangles: number[] = [];
  let guard = 0;
  while (remaining.length > 3 && guard < n * n) {
    guard++;
    let earFound = false;
    for (let i = 0; i < remaining.length; i++) {
      const prev = remaining[(i - 1 + remaining.length) % remaining.length];
      const cur = remaining[i];
      const next = remaining[(i + 1) % remaining.length];
      const a = points[prev];
      const b = points[cur];
      const c = points[next];
      if (same2(a, b) || same2(b, c) || same2(a, c)) continue;
      const cross = (b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y);
      if (cross <= 1e-12) continue;
      const centroid = { x: (a.x + b.x + c.x) / 3, y: (a.y + b.y + c.y) / 3 };
      if (!inSolid(centroid)) continue;
      let contains = false;
      for (const idx of remaining) {
        if (idx === prev || idx === cur || idx === next) continue;
        if (same2(points[idx], a) || same2(points[idx], b) || same2(points[idx], c)) continue;
        if (isPointInTriangle(points[idx], a, b, c)) {
          contains = true;
          break;
        }
      }
      if (contains) continue;
      triangles.push(prev, cur, next);
      remaining.splice(i, 1);
      earFound = true;
      break;
    }
    if (!earFound) break;
  }

  if (remaining.length === 3) {
    const a = points[remaining[0]];
    const b = points[remaining[1]];
    const c = points[remaining[2]];
    if (!same2(a, b) && !same2(b, c) && !same2(a, c)) {
      const centroid = { x: (a.x + b.x + c.x) / 3, y: (a.y + b.y + c.y) / 3 };
      if (inSolid(centroid)) {
        const cross = (b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y);
        if (cross > 1e-12) triangles.push(remaining[0], remaining[1], remaining[2]);
        else if (cross < -1e-12) triangles.push(remaining[0], remaining[2], remaining[1]);
      }
    }
  }
  return triangles;
}

/** 凸なクリップ多角形で切る（Sutherland–Hodgman）。 */
export function clipPolygon(subject: Point2D[], clip: Point2D[]): Point2D[] {
  if (subject.length < 3 || clip.length < 3) return [];
  const clipCcw = signedArea2(clip) >= 0;
  let output = subject;
  for (let i = 0; i < clip.length; i++) {
    const a = clip[i];
    const b = clip[(i + 1) % clip.length];
    const input = output;
    output = [];
    if (input.length === 0) return [];
    let prev = input[input.length - 1];
    for (const cur of input) {
      const curIn = clipCcw ? orient(a, b, cur) >= -EPS : orient(a, b, cur) <= EPS;
      const prevIn = clipCcw ? orient(a, b, prev) >= -EPS : orient(a, b, prev) <= EPS;
      if (curIn) {
        if (!prevIn) {
          const hit = segmentIntersection(prev, cur, a, b);
          if (hit) output.push(hit);
        }
        output.push(cur);
      } else if (prevIn) {
        const hit = segmentIntersection(prev, cur, a, b);
        if (hit) output.push(hit);
      }
      prev = cur;
    }
  }
  return output.length >= 3 ? output : [];
}
